import { getLanguageFlag } from '@/lib/languageFlags'

export interface Country {
  code: string
  dialCode: string
  name: string
  flag: string
}

// Comprehensive country list with dial codes and flags
// Priority countries (most common in EU) are listed first
const COUNTRY_DATA = [
  // Priority countries (most common in EU)
  { code: 'NL', dialCode: '+31', name: 'Netherlands' },
  { code: 'BE', dialCode: '+32', name: 'Belgium' },
  { code: 'DE', dialCode: '+49', name: 'Germany' },
  { code: 'FR', dialCode: '+33', name: 'France' },
  { code: 'GB', dialCode: '+44', name: 'United Kingdom' },
  { code: 'ES', dialCode: '+34', name: 'Spain' },
  { code: 'IT', dialCode: '+39', name: 'Italy' },
  { code: 'PT', dialCode: '+351', name: 'Portugal' },
  { code: 'AT', dialCode: '+43', name: 'Austria' },
  { code: 'CH', dialCode: '+41', name: 'Switzerland' },
  { code: 'IE', dialCode: '+353', name: 'Ireland' },
  { code: 'US', dialCode: '+1', name: 'United States' },
  // Additional European countries
  { code: 'PL', dialCode: '+48', name: 'Poland' },
  { code: 'SE', dialCode: '+46', name: 'Sweden' },
  { code: 'NO', dialCode: '+47', name: 'Norway' },
  { code: 'DK', dialCode: '+45', name: 'Denmark' },
  { code: 'FI', dialCode: '+358', name: 'Finland' },
  { code: 'GR', dialCode: '+30', name: 'Greece' },
  { code: 'CZ', dialCode: '+420', name: 'Czech Republic' },
  { code: 'HU', dialCode: '+36', name: 'Hungary' },
  { code: 'RO', dialCode: '+40', name: 'Romania' },
  { code: 'BG', dialCode: '+359', name: 'Bulgaria' },
  { code: 'HR', dialCode: '+385', name: 'Croatia' },
  { code: 'SK', dialCode: '+421', name: 'Slovakia' },
  { code: 'SI', dialCode: '+386', name: 'Slovenia' },
  { code: 'LT', dialCode: '+370', name: 'Lithuania' },
  { code: 'LV', dialCode: '+371', name: 'Latvia' },
  { code: 'EE', dialCode: '+372', name: 'Estonia' },
  { code: 'LU', dialCode: '+352', name: 'Luxembourg' },
  { code: 'MT', dialCode: '+356', name: 'Malta' },
  { code: 'CY', dialCode: '+357', name: 'Cyprus' },
  // Other common countries
  { code: 'CA', dialCode: '+1', name: 'Canada' },
  { code: 'AU', dialCode: '+61', name: 'Australia' },
  { code: 'NZ', dialCode: '+64', name: 'New Zealand' },
  { code: 'JP', dialCode: '+81', name: 'Japan' },
  { code: 'KR', dialCode: '+82', name: 'South Korea' },
  { code: 'CN', dialCode: '+86', name: 'China' },
  { code: 'IN', dialCode: '+91', name: 'India' },
  { code: 'BR', dialCode: '+55', name: 'Brazil' },
  { code: 'MX', dialCode: '+52', name: 'Mexico' },
  { code: 'AR', dialCode: '+54', name: 'Argentina' },
  { code: 'ZA', dialCode: '+27', name: 'South Africa' },
  { code: 'AE', dialCode: '+971', name: 'United Arab Emirates' },
  { code: 'SA', dialCode: '+966', name: 'Saudi Arabia' },
  { code: 'TR', dialCode: '+90', name: 'Turkey' },
  { code: 'RU', dialCode: '+7', name: 'Russia' },
  { code: 'UA', dialCode: '+380', name: 'Ukraine' },
  { code: 'IL', dialCode: '+972', name: 'Israel' },
  { code: 'SG', dialCode: '+65', name: 'Singapore' },
  { code: 'HK', dialCode: '+852', name: 'Hong Kong' },
  { code: 'TH', dialCode: '+66', name: 'Thailand' },
  { code: 'MY', dialCode: '+60', name: 'Malaysia' },
  { code: 'ID', dialCode: '+62', name: 'Indonesia' },
  { code: 'PH', dialCode: '+63', name: 'Philippines' },
  { code: 'VN', dialCode: '+84', name: 'Vietnam' },
] as const

export const COUNTRIES: Country[] = COUNTRY_DATA.map((country) => ({
  ...country,
  flag: getLanguageFlag(`xx-${country.code}`),
}))

// Mirrors the API rule in services/payment/tax_ids.py: VAT/tax ID is required
// for every country with a Stripe tax type except NL (national flow).
const VAT_COUNTRIES = new Set([
  // EU
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // Non-EU with a mapped Stripe tax type
  'AD', 'AE', 'AU', 'BR', 'CA', 'CH', 'CL', 'CO', 'GB', 'HK', 'IL', 'IN', 'IS',
  'JP', 'KR', 'LI', 'MX', 'MY', 'NO', 'NZ', 'SA', 'SG', 'TH', 'TR', 'TW', 'US', 'ZA',
])

/**
 * Whether a VAT/tax ID is required for this billing country.
 */
export function vatRequired(code: string | null | undefined): boolean {
  return !!code && code.toUpperCase() !== 'NL' && VAT_COUNTRIES.has(code.toUpperCase())
}

/**
 * Find a country by its ISO 3166-1 alpha-2 code.
 */
export function getCountryByCode(code: string): Country | undefined {
  return COUNTRIES.find((c) => c.code === code)
}
